// ============================================================
// PROFILE STRENGTH SIDEBAR COMPONENT
// 
// Unified sidebar for ClientProfilePage that shows:
// - Single "Profile Strength" metric (replaces both readiness & completion)
// - Visual ring/progress indicator
// - Missing fields list with priority
// - Quick stats summary
// 
// Usage: Replace the existing two-metric sidebar with this component
// ============================================================

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { getProfileStrength } from '@/services/clientProfileService';
import { calculateProfileStrength } from '@/utils/calculateProfileStrength';
import { 
  AlertCircle, 
  CheckCircle, 
  ChevronDown, 
  ChevronUp,
  TrendingUp,
  Building2,
  Bed,
  Users,
  MapPin
} from 'lucide-react';

// ============================================================
// DVA DESIGN SYSTEM COLORS
// ============================================================
const DVA_COLORS = {
  navy: '#18365E',
  orange: '#FF6E4C',
  orangeLight: '#FFF4F1',
  slate: '#717182',
  slateLight: '#F5F7FA',
  border: '#E5E7EB',
  success: '#10B981',
  successLight: '#ECFDF5',
  warning: '#F59E0B',
  warningLight: '#FFFBEB',
  error: '#EF4444',
  errorLight: '#FEF2F2',
};

// ============================================================
// CIRCULAR PROGRESS RING
// ============================================================
function ProgressRing({ percentage, size = 120, strokeWidth = 10, color }) {
  const radius = (size - strokeWidth) / 2;
  const circumference = radius * 2 * Math.PI;
  const offset = circumference - (percentage / 100) * circumference;

  return (
    <div className="relative" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="transform -rotate-90">
        {/* Background circle */}
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke={DVA_COLORS.border}
          strokeWidth={strokeWidth}
        />
        {/* Progress circle */}
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke={color || DVA_COLORS.orange}
          strokeWidth={strokeWidth}
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          strokeLinecap="round"
          style={{ transition: 'stroke-dashoffset 0.5s ease-in-out' }}
        />
      </svg>
      {/* Center text */}
      <div 
        className="absolute inset-0 flex flex-col items-center justify-center"
      >
        <span 
          className="text-3xl font-bold" 
          style={{ color: DVA_COLORS.navy }}
        >
          {percentage}%
        </span>
      </div>
    </div>
  );
}

// ============================================================
// PROFILE STRENGTH SIDEBAR
// ============================================================
export default function ProfileStrengthSidebar({ 
  profile, 
  strength,
  isLoading = false,
  onMissingFieldClick = null  // ✅ NEW: Callback when user clicks a missing field
}) {
  const [showAllMissing, setShowAllMissing] = useState(false);

  // Always derive score from the current profile (same util as EA Personalize card).
  // Ignore API strength.score — /strength and /readiness use different formulas and
  // previously caused Personalize (e.g. 67%) vs this sidebar (e.g. 74%) mismatches.
  const strengthData = useMemo(() => {
    const localStrength = calculateProfileStrength(profile);
    if (!strength) return localStrength;

    return {
      ...localStrength,
      suggestions: strength.suggestions || localStrength.suggestions,
    };
  }, [profile, strength]);

  // Get color based on score
  const getScoreColor = (score) => {
    if (score >= 75) return DVA_COLORS.success;
    if (score >= 50) return DVA_COLORS.warning;
    if (score >= 25) return DVA_COLORS.orange;
    return DVA_COLORS.error;
  };

  const scoreColor = strengthData.color || getScoreColor(strengthData.score);

  // Missing fields to display
  const missingFields = strengthData.missingFields || [];
  const displayedMissing = showAllMissing ? missingFields : missingFields.slice(0, 3);

  if (isLoading) {
    return (
      <div className="bg-white rounded-2xl border p-6" style={{ borderColor: DVA_COLORS.border }}>
        <div className="animate-pulse">
          <div className="h-4 bg-gray-200 rounded w-1/2 mb-4"></div>
          <div className="h-24 bg-gray-200 rounded-full w-24 mx-auto mb-4"></div>
          <div className="h-4 bg-gray-200 rounded w-3/4 mx-auto"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* ============================================================ */}
      {/* PROFILE STRENGTH CARD */}
      {/* ============================================================ */}
      <div 
        className="bg-white rounded-2xl border p-6"
        style={{ borderColor: DVA_COLORS.border }}
      >
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-semibold" style={{ color: DVA_COLORS.navy }}>
            Profile Strength
          </h3>
          <span 
            className="text-xs font-medium px-2 py-1 rounded-full"
            style={{ 
              backgroundColor: scoreColor + '20',
              color: scoreColor 
            }}
          >
            {strengthData.label || 'Good'}
          </span>
        </div>

        {/* Progress Ring */}
        <div className="flex justify-center mb-4">
          <ProgressRing 
            percentage={strengthData.score || 0} 
            color={scoreColor}
          />
        </div>

        {/* Score Breakdown */}
        <div className="space-y-2 mb-4">
          <ScoreBreakdownRow 
            label="Critical fields" 
            score={strengthData.breakdown?.critical || 0}
            weight="60%"
          />
          <ScoreBreakdownRow 
            label="Optional fields" 
            score={strengthData.breakdown?.optional || 0}
            weight="30%"
          />
          <ScoreBreakdownRow 
            label="Data quality" 
            score={strengthData.breakdown?.quality || 0}
            weight="10%"
          />
        </div>

        {/* Divider */}
        <div className="border-t my-4" style={{ borderColor: DVA_COLORS.border }}></div>

        {/* Missing Fields */}
        {missingFields.length > 0 ? (
          <div>
            <div className="flex items-center gap-2 mb-2">
              <AlertCircle className="w-4 h-4" style={{ color: DVA_COLORS.orange }} />
              <span className="text-xs font-medium" style={{ color: DVA_COLORS.navy }}>
                Missing fields ({missingFields.length})
              </span>
            </div>
            <ul className="space-y-1">
              {displayedMissing.map((field, idx) => (
                <li key={idx}>
                  {onMissingFieldClick ? (
                    <button
                      type="button"
                      onClick={() => onMissingFieldClick(field)}
                      className="text-xs flex items-center gap-2 hover:underline cursor-pointer text-left"
                      style={{ color: DVA_COLORS.orange }}
                    >
                      <span className="w-1 h-1 rounded-full bg-current flex-shrink-0"></span>
                      {field}
                    </button>
                  ) : (
                    <span
                      className="text-xs flex items-center gap-2"
                      style={{ color: DVA_COLORS.orange }}
                    >
                      <span className="w-1 h-1 rounded-full bg-current"></span>
                      {field}
                    </span>
                  )}
                </li>
              ))}
            </ul>
            {missingFields.length > 3 && (
              <button
                onClick={() => setShowAllMissing(!showAllMissing)}
                className="mt-2 text-xs font-medium flex items-center gap-1"
                style={{ color: DVA_COLORS.slate }}
              >
                {showAllMissing ? (
                  <>Show less <ChevronUp className="w-3 h-3" /></>
                ) : (
                  <>Show {missingFields.length - 3} more <ChevronDown className="w-3 h-3" /></>
                )}
              </button>
            )}
          </div>
        ) : (
          <div className="flex items-center gap-2">
            <CheckCircle className="w-4 h-4" style={{ color: DVA_COLORS.success }} />
            <span className="text-xs" style={{ color: DVA_COLORS.success }}>
              All critical fields complete!
            </span>
          </div>
        )}
      </div>

      {/* ============================================================ */}
      {/* QUICK STATS CARD */}
      {/* ============================================================ */}
      <div 
        className="bg-white rounded-2xl border p-6"
        style={{ borderColor: DVA_COLORS.border }}
      >
        <h3 className="text-sm font-semibold mb-4" style={{ color: DVA_COLORS.navy }}>
          Profile Summary
        </h3>

        <div className="space-y-3">
          {/* Organization */}
          <QuickStat 
            icon={Building2}
            label="Organization"
            value={profile?.organization_name || profile?.client_name || '—'}
          />

          {/* Type */}
          <QuickStat 
            icon={TrendingUp}
            label="Type"
            value={profile?.organization_type || '—'}
          />

          {/* Beds */}
          <QuickStat 
            icon={Bed}
            label="Beds"
            value={profile?.beds || profile?.total_beds ? Number(profile?.beds || profile?.total_beds).toLocaleString() : '—'}
          />

          {/* VBC Lives */}
          <QuickStat 
            icon={Users}
            label="VBC Lives"
            value={profile?.lives_under_vbc_agreements || profile?.lives_under_vbc ? Number(profile?.lives_under_vbc_agreements || profile?.lives_under_vbc).toLocaleString() : '—'}
          />
        </div>
      </div>
    </div>
  );
}

// ============================================================
// SCORE BREAKDOWN ROW
// ============================================================
function ScoreBreakdownRow({ label, score, weight }) {
  return (
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-2">
        <span className="text-xs" style={{ color: DVA_COLORS.slate }}>
          {label}
        </span>
        <span className="text-xs" style={{ color: DVA_COLORS.border }}>
          ({weight})
        </span>
      </div>
      <div className="flex items-center gap-2">
        <div 
          className="w-16 h-1.5 rounded-full"
          style={{ backgroundColor: DVA_COLORS.border }}
        >
          <div 
            className="h-1.5 rounded-full transition-all duration-300"
            style={{ 
              width: `${score}%`,
              backgroundColor: score >= 75 ? DVA_COLORS.success : 
                              score >= 50 ? DVA_COLORS.warning : DVA_COLORS.orange
            }}
          />
        </div>
        <span 
          className="text-xs font-medium w-8 text-right"
          style={{ color: DVA_COLORS.navy }}
        >
          {score}%
        </span>
      </div>
    </div>
  );
}

// ============================================================
// QUICK STAT ROW
// ============================================================
function QuickStat({ icon: Icon, label, value }) {
  const isEmpty = !value || value === '—';

  return (
    <div className="flex items-center justify-between gap-2">
      <div className="flex items-center gap-2 min-w-0">
        <Icon
          className="w-4 h-4 shrink-0"
          style={{ color: isEmpty ? DVA_COLORS.border : DVA_COLORS.slate }}
        />
        <span
          className="text-xs truncate"
          style={{ color: DVA_COLORS.slate }}
        >
          {label}
        </span>
      </div>
      <span
        className="text-sm font-medium text-right truncate"
        style={{ color: isEmpty ? DVA_COLORS.border : DVA_COLORS.navy }}
        title={value} // Show full value on hover
      >
        {value}
      </span>
    </div>
  );
}

// ============================================================
// LOCAL STRENGTH CALCULATION — re-exported from shared util
// ============================================================
export { calculateProfileStrength };

// ============================================================
// HOOK FOR FETCHING PROFILE STRENGTH FROM API
// ============================================================
export function useProfileStrength(clientId) {
  const [strength, setStrength] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchStrength = useCallback(async () => {
    if (!clientId) {
      setStrength(null);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const data = await getProfileStrength(clientId);
      setStrength(data);
      if (!data) {
        setError('Failed to fetch profile strength');
      }
    } catch (err) {
      console.error('[ProfileStrength] Fetch error:', err);
      setError(err.message);
      setStrength(null);
    } finally {
      setLoading(false);
    }
  }, [clientId]);

  useEffect(() => {
    fetchStrength();
  }, [fetchStrength]);

  return { strength, loading, error, refetch: fetchStrength };
}